import { Gamepad2, Cloud, Zap, Users, ShieldCheck, Smartphone } from "lucide-react";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { FaRocket } from "react-icons/fa";

const WhyChooseUs = () => {

useEffect(() => {
  const loadAOS = async () => {
    const AOS = await import('aos');
    AOS.init({
      duration:500,
      easing: "ease-out-cubic",
      once: true,
      offset: 100
    });
  };
  loadAOS();
}, []);


const features = [
  {
    title: "Instant Play - No Downloads",
    icon: <Zap className="w-10 h-10 text-yellow-400" aria-hidden="true" />,
    desc: "Save your device storage! Our advanced HTML5 technology allows you to play free online instantly without downloading large files or installing suspicious software.",
  },
  {
    title: "100% Safe Online Games",
    icon: <ShieldCheck className="w-10 h-10 text-green-400" aria-hidden="true" />,
    desc: "Your safety is our priority. Game Zone provides a secure environment for free online games. No hidden fees, no subscriptions, and absolutely no malware risks.",
  },
  {
    title: "Mobile Optimized Experience",
    icon: <Smartphone className="w-10 h-10 text-blue-400" aria-hidden="true" />,
    desc: "Play seamlessly on any device. Whether you are using an iPhone, Android, Tablet, or PC, our responsive design ensures smooth gameplay for all our online games.",
  },
  {
    title: "Massive Game Library",
    icon: <Gamepad2 className="w-10 h-10 text-pink-400" aria-hidden="true" />,
    desc: "Never get bored with our huge collection. We update our library daily with the latest action, racing, and puzzle titles so you can play free online games continuously.",
  },
];

 const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": features.map((feature, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": feature.title,
      "description": feature.desc
    }))
};

  return (
    <section className="bg-gradient-to-b lg:px-8 from-[#0f172a] to-[#1e293b] sm:py-16 py-9">
       <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4">
        <h3
          className="text-2xl text-center sm:text-3xl md:text-4xl xl:text-5xl 
          font-extrabold text-white mb-5 flex items-center justify-center gap-3"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <FaRocket className="text-cyan-400 animate-bounce sm:animate-none sm:hover:-translate-y-1         transition-transform duration-300" />
        
          Why Choose{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400         to-purple-300">
            GameZone
          </span>
        </h3>
        <p
          className="text-gray-400 text-center sm:text-lg text-[16px] mb-12 max-w-4xl mx-auto leading-relaxed"
          data-aos="fade-up" data-aos-delay="200"
        >
          Game Zone is the most trusted destination to <strong>play free online games</strong>. 
          Unlike other sites, we focus on providing a safe, fast, and ad-lite experience. 
          Our platform is optimized for performance, ensuring lag-free gaming on both low-end mobile devices and high-end PCs, so you can <strong>play free online</strong> anywhere.
        </p>
      

        <ul
          className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-8"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          {features.map((feature, index) => (
            <li
              key={index}
              data-aos="zoom-in"
              data-aos-delay={`${300 + index * 100}`}
              className="relative bg-[#1e293b] rounded-2xl cursor-pointer p-6 text-center
                         border border-transparent hover:border-cyan-400/50
                         transition-all duration-500 group
                         shadow-[0_0_20px_rgba(0,0,0,0.4)] 
                         hover:shadow-[0_0_35px_rgba(0,200,255,0.4)]
                         hover:scale-105 hover:-translate-y-2 overflow-hidden"
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-400/20 blur-3xl rounded-full"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/20 blur-3xl rounded-full"></div>
              </div>

              <div
                className="flex justify-center mb-4 relative z-10 
                           group-hover:scale-110 transition-transform duration-300"
                data-aos="flip-up"
                data-aos-delay={`${350 + index * 100}`}
              >
                {feature.icon}
              </div>

              <h3
                className="sm:text-xl text-lg font-bold text-white mb-2 relative z-10
                           group-hover:text-cyan-300 transition-colors duration-300"
                data-aos="fade-up"
                data-aos-delay={`${400 + index * 100}`}
              >
                {feature.title}
              </h3>

              <p
                className="text-gray-400 text-sm relative z-10 group-hover:text-gray-300 transition-colors duration-300"
                data-aos="fade-up"
                data-aos-delay={`${450 + index * 100}`}
              >
                {feature.desc}
              </p>

              <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default WhyChooseUs;
